import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Logo } from "../components/tabon/Logo";
import { api } from "../lib/api";

const VENUE_LOGIN_HREF = "https://dashboard.tabon.app";

function Card({ title, children }) {
  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 mb-4">
      {title && <h2 className="text-lg font-bold text-white mb-3">{title}</h2>}
      <div className="text-[#A0A0A0] text-sm leading-relaxed">
        {children}
      </div>
    </div>
  );
}

function NumList({ items }) {
  return (
    <ol className="space-y-3">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3">
          <span className="text-[#9D00FF] font-bold flex-shrink-0 w-5">{i + 1}.</span>
          <span>{item}</span>
        </li>
      ))}
    </ol>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Terms() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/public/terms/${slug}`);
        setData(d);
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const nav = (
    <header className="relative flex items-center justify-between px-6 md:px-12 py-5">
      <a href="/" aria-label="Back to TabOn home">
        <Logo size="md" />
      </a>
      <a href="/" className="text-sm text-[#A0A0A0] hover:text-white transition">
        ← Back to TabOn
      </a>
    </header>
  );

  const footer = (
    <footer className="relative border-t border-white/5">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 grid gap-8 md:grid-cols-3 items-start">
        <div>
          <Logo size="md" />
          <p className="text-sm text-[#A0A0A0] mt-3">Turn Every Visit Into The Next One.</p>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-2 md:justify-center">
          <a href="/" className="text-sm text-[#A0A0A0] hover:text-white transition">Home</a>
          <a href={VENUE_LOGIN_HREF} className="text-sm text-[#A0A0A0] hover:text-white transition">Venue Login</a>
          <a href="/privacy" className="text-sm text-[#A0A0A0] hover:text-white transition">Privacy Policy</a>
        </div>
        <div className="md:text-right">
          <a href="mailto:cheers@tabon.app" className="text-sm text-white hover:text-[#9D00FF] transition">
            cheers@tabon.app
          </a>
        </div>
      </div>
      <div className="border-t border-white/5 py-4">
        <div className="max-w-6xl mx-auto px-6 md:px-12 text-xs text-[#A0A0A0] text-center">
          <div>
            © 2026 TabOn Labs · UDYAM-RJ-33-0131727 · Udaipur, Rajasthan ·{" "}
            <a href="mailto:cheers@tabon.app" className="hover:text-white transition">cheers@tabon.app</a>
          </div>
          <div className="text-[11px] text-[#6b6b6b] mt-1">
            TabOn Labs is registered under the Ministry of Micro, Small and Medium Enterprises, Government of India.
          </div>
        </div>
      </div>
    </footer>
  );

  if (loading) {
    return (
      <div className="min-h-screen tb-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 tb-grid-bg opacity-30 pointer-events-none" />
        {nav}
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 rounded-full border-2 border-[#9D00FF] border-t-transparent animate-spin" />
            <p className="text-[#A0A0A0] text-sm">Loading terms…</p>
          </div>
        </div>
        {footer}
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen tb-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 tb-grid-bg opacity-30 pointer-events-none" />
        {nav}
        <div className="flex items-center justify-center min-h-[60vh] px-6">
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-8 text-center max-w-md">
            <p className="text-2xl mb-3">📄</p>
            <h2 className="text-white font-bold text-lg mb-2">Terms not available</h2>
            <p className="text-[#A0A0A0] text-sm">These terms are not available for this venue.</p>
          </div>
        </div>
        {footer}
      </div>
    );
  }

  const {
    venue_name,
    venue_logo_url,
    loyalty_enabled,
    bottle_locker_enabled,
    loyalty_terms,
    bottle_locker_terms,
    points_rate,
    points_expiry_months,
    redemption_max_percent,
    redemption_min_bill,
    redemption_cooldown,
    last_updated,
  } = data;

  // Part A — General Programme Terms (always shown)
  const generalTerms = [
    <>
      This loyalty programme is operated by{" "}
      <span className="text-white font-medium">{venue_name}</span> ("the Venue") and
      powered by TabOn ("TabOn Labs"). TabOn provides the technology; the Venue
      operates the programme.
    </>,
    <>
      Loyalty points are earned on eligible purchases at a rate of{" "}
      <span className="text-white font-medium">{points_rate} points per ₹100 spent</span>,
      subject to minimum spend and exclusions set by the Venue.
    </>,
    <>
      <span className="text-white font-medium">1 loyalty point = ₹1 redeemable value</span>{" "}
      against future bills at {venue_name}. Points have no cash value, cannot be
      transferred, and are not legal tender.
    </>,
    ...(redemption_max_percent < 100 ? [
      <>
        Up to <span className="text-white font-medium">{redemption_max_percent}%</span> of any
        bill may be paid using loyalty points. The remaining balance must be paid by
        other means.
      </>,
    ] : []),
    ...(redemption_min_bill > 0 ? [
      <>
        A minimum bill value of{" "}
        <span className="text-white font-medium">₹{redemption_min_bill}</span> is required to
        redeem points.
      </>,
    ] : []),
    ...(redemption_cooldown === "same_day" ? [
      <>
        Points earned on a visit become{" "}
        <span className="text-white font-medium">redeemable from the following day</span>.
      </>,
    ] : []),
    <>
      Points can only be redeemed at the billing counter by authorised staff.
    </>,
    points_expiry_months ? (
      <>
        Points expire{" "}
        <span className="text-white font-medium">{points_expiry_months} month{points_expiry_months !== 1 ? "s" : ""}</span>{" "}
        after earning if not redeemed. A WhatsApp reminder will be sent before
        expiry.
      </>
    ) : (
      <>
        Points do not currently expire.{" "}
        <span className="text-white font-medium">{venue_name}</span> reserves the right
        to introduce an expiry policy with 30 days' prior notice.
      </>
    ),
    <>
      {venue_name} and TabOn reserve the right to modify, suspend, or discontinue
      the loyalty programme with reasonable notice to active members.
    </>,
    <>
      By joining, you consent to receive loyalty-related WhatsApp messages. Reply{" "}
      <span className="font-mono bg-white/5 px-1.5 py-0.5 rounded text-xs">STOP</span>{" "}
      to opt out. See{" "}
      <a href="/privacy" className="text-[#9D00FF] hover:underline">
        tabon.app/privacy
      </a>{" "}
      for data details.
    </>,
    <>
      Points issued in error may be reversed by the Venue.
    </>,
  ];

  // Part C — Bottle locker standard terms
  const bottleLockerTerms = [
    <>
      The bottle storage facility ("Bottle Locker") is operated by{" "}
      <span className="text-white font-medium">{venue_name}</span> under its applicable
      excise licence. TabOn provides tracking technology only and bears no liability
      for physical storage.
    </>,
    <>
      Customers must verify identity via OTP sent to their registered WhatsApp number
      before each serve or tab opening.
    </>,
    <>
      {venue_name} is not liable for loss, theft, breakage, or damage unless caused
      by gross negligence by venue staff.
    </>,
    <>
      Bottle locker availability may be restricted during private events or
      circumstances beyond the venue's control.
    </>,
    <>
      Bottles unclaimed for more than 90 days may be disposed of in accordance with
      applicable excise regulations.
    </>,
    <>
      Alcohol service subject to applicable state excise laws including age
      verification (minimum 25 years in Rajasthan).
    </>,
  ];

  return (
    <div className="min-h-screen tb-hero-gradient relative overflow-hidden">
      <div className="absolute inset-0 tb-grid-bg opacity-30 pointer-events-none" />

      {nav}

      {/* Hero */}
      <section className="relative px-6 md:px-12 pt-12 pb-10 max-w-4xl mx-auto">
        {venue_logo_url && (
          <img
            src={venue_logo_url}
            alt={venue_name}
            className="h-14 object-contain mb-4"
          />
        )}
        <div className="inline-flex items-center gap-2 bg-[#9D00FF]/10 border border-[#9D00FF]/30 rounded-full px-3 py-1 text-xs text-[#D78CFF] mb-4">
          Legal
        </div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight text-white">
          {venue_name}
        </h1>
        <p className="text-[#A0A0A0] text-sm mt-1">
          Loyalty Programme Terms &amp; Conditions
        </p>
        {last_updated && (
          <p className="text-[#A0A0A0] text-xs mt-1">
            Last updated: {formatDate(last_updated)}
          </p>
        )}
      </section>

      {/* Content */}
      <main className="relative px-6 md:px-12 pb-20 max-w-4xl mx-auto">

        {/* Part A — General Programme Terms */}
        <Card title="Part A — General Programme Terms">
          <NumList items={generalTerms} />
        </Card>

        {/* Part B — Venue Rules (only if the venue set any). loyalty_terms is a
            list (legacy strings are split server-side); [] must render no card. */}
        {loyalty_terms?.length > 0 && (
          <Card title={`Programme Rules set by ${venue_name}`}>
            <NumList items={loyalty_terms} />
          </Card>
        )}

        {/* Part C — Bottle Storage Terms (only if bottle_locker enabled) */}
        {bottle_locker_enabled && (
          <Card title="Part C — Bottle Storage Terms">
            <NumList items={bottleLockerTerms} />
            {bottle_locker_terms && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <h3 className="text-white font-semibold text-sm mb-2">Additional Rules</h3>
                <p style={{ whiteSpace: "pre-wrap" }}>{bottle_locker_terms}</p>
              </div>
            )}
          </Card>
        )}

        {/* Powered by */}
        <p className="text-center text-xs text-[#A0A0A0] mt-6">
          Loyalty programme powered by{" "}
          <a href="https://tabon.app" className="text-[#9D00FF] hover:underline font-semibold">
            TabOn
          </a>
          {" "}·{" "}
          <a href="/privacy" className="text-[#A0A0A0] hover:text-white transition">
            Privacy Policy
          </a>
        </p>
      </main>

      {footer}
    </div>
  );
}
